import { useMemo, useState } from 'react';
import { ConsignmentApiModel, Dimensions, PackageApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import {
  IsInternational,
  ItemServiceSupportsMultiParcelShipment
} from '../../../lib/helpers/SmartSendSummaryGenerator';
import { DimensionsSelection } from '../DimensionsSelection';
import { CustomInner } from '../grouping/CustomInner';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { DimensionsAreValid } from './DimensionsAreValid';
import { GenerateGroupSummary } from './GenerateGroupSummary';
import { ConsignmentSummary } from './consignments/ConsignmentSummary';
import { MultiConsignmentUnsupported } from './consignments/MultiConsignmentUnsupported';
import { GroupingPanel } from './GroupingPanel';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { GenerateConsignmentFromItem } from './consignments/GenerateConsignmentFromItem';
import { P2GBox } from '@parcel2go/core-ui';

export interface props {
  item: SmartSendItemApiModel;
  onUpdate: (c: ConsignmentApiModel) => void;
  onClose: () => void;
  dimensionsRequired?: boolean;
  hideDimensionsIfNotRequired: boolean;
  getPackages: () => Promise<PackageApiModel[]>;
  onDimensionsUpdate?: (dimensions: Dimensions) => void;
  dimensions: Dimensions;
}

export const ConsignmentDetails: React.FC<props> = (props) => {
  const [expand, setExpand] = useState(IsInternational(props.item));
  const serviceSupportsConsignments = ItemServiceSupportsMultiParcelShipment(props.item);

  const valid = useMemo(
    () => DimensionsAreValid(props.dimensions, props.dimensionsRequired),
    [props.dimensions, props.dimensionsRequired]
  );

  const group = useMemo<SmartSendItemGroup>(() => {
    return { Items: [props.item], Title: '', IsGroupable: false, States: [], Hidden: false };
  }, [props.item]);

  const groupSummary = useMemo(() => GenerateGroupSummary(group.Items), [group]);
  const consignment = props.item.Consignment;
  const consignmentParcels = consignment?.Parcels?.length || 1;
  const splitConsignmentEnabled = SettingsCache.settings.Features.includes('ParcelSplitting');

  const onUpdate = (c: ConsignmentApiModel) => {
    if (props.onUpdate) {
      props.onUpdate(c);
    }
  };

  const sendAsSingle = () => {
    onUpdate(null);
    setExpand(IsInternational(props.item));
  };

  const generateConsignment = (qty: number) => {
    if (qty === 1) {
      sendAsSingle();
      return;
    }
    onUpdate(GenerateConsignmentFromItem(props.item, props.dimensions, qty));
  };

  if (consignment && !serviceSupportsConsignments) {
    return <MultiConsignmentUnsupported reset={sendAsSingle} cancel={props.onClose} />;
  }

  return (
    <>
      <P2GBox mb={2}>
        <ConsignmentSummary
          serviceSupportsConsignments={serviceSupportsConsignments}
          consignment={consignment}
          consignmentParcels={consignmentParcels}
          expand={expand}
          setExpand={setExpand}
          group={group}
          groupSummary={groupSummary}
          onClose={props.onClose}
          valid={valid}
          generateConsignment={generateConsignment}
          splitConsignmentControl={
            splitConsignmentEnabled ? <GroupingPanel group={group} onComplete={props.onClose} /> : undefined
          }
        />
      </P2GBox>
      {consignment && expand ? (
        <CustomInner
          allowContentChange={true}
          dimensionsRequired={props.dimensionsRequired}
          key={groupSummary.totalQuantity}
          totalParcelValue={group.Items[0].Value}
          allowInlineDimensions={true}
          getPackages={props.getPackages}
          allowExpanding={true}
          group={group}
          onChange={onUpdate}
          consignment={props.item.Consignment}
          dims={props.dimensions}
          hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
          itemName="Parcel"
        />
      ) : (
        <DimensionsSelection
          startMinimised={false}
          dimensions={props.dimensions}
          onUpdate={props.onDimensionsUpdate}
          getPackages={props.getPackages}
          hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
          dimensionsRequired={props.dimensionsRequired}
        />
      )}
    </>
  );
};
